import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users, UserPlus, Activity, Globe } from 'lucide-react';
import { api } from '../../../services/api';
import { MetricCard } from '../cards/MetricCard';
import { LineChart } from '../charts/LineChart';
import { PieChart } from '../charts/PieChart';
import { BarChart } from '../charts/BarChart';

// Country code to name mapping (subset)
const COUNTRY_NAMES: Record<string, string> = {
  US: 'United States',
  CA: 'Canada',
  GB: 'United Kingdom',
  DE: 'Germany',
  FR: 'France',
  AU: 'Australia',
  JP: 'Japan',
  CN: 'China',
  IN: 'India',
  BR: 'Brazil',
  MX: 'Mexico',
  ES: 'Spain',
  IT: 'Italy',
  NL: 'Netherlands',
  SE: 'Sweden',
  NO: 'Norway',
  DK: 'Denmark',
  FI: 'Finland',
  PL: 'Poland',
  AT: 'Austria',
  CH: 'Switzerland',
  BE: 'Belgium',
  IE: 'Ireland',
  PT: 'Portugal',
  Unknown: 'Unknown',
};

const AUTH_PROVIDER_COLORS: Record<string, string> = {
  email: '#6b7280',
  google: '#ea4335',
  facebook: '#1877f2',
  apple: '#000000',
};

const TIER_COLORS: Record<string, string> = {
  free: '#9ca3af',
  hunter: '#0077B6',
  pro: '#10b981',
  dealer: '#f59e0b',
};

export default function UserAnalytics() {
  const [selectedCountry, setSelectedCountry] = useState<string>('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-user-stats', selectedCountry],
    queryFn: () => api.getAdminUserStats({ country: selectedCountry || undefined }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        Failed to load analytics. Please try again.
      </div>
    );
  }

  if (!data) return null;

  // Prepare chart data
  const growthChartData = data.growth_data.map((d) => ({
    date: new Date(d.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    signups: d.signups,
    active: d.active,
  }));

  const authProviderData = Object.entries(data.auth_provider_distribution)
    .filter(([, count]) => count > 0)
    .map(([provider, count]) => ({
      name: provider.charAt(0).toUpperCase() + provider.slice(1),
      value: count,
      color: AUTH_PROVIDER_COLORS[provider],
    }));

  const tierData = Object.entries(data.tier_distribution)
    .filter(([, count]) => count > 0)
    .map(([tier, count]) => ({
      name: tier.charAt(0).toUpperCase() + tier.slice(1),
      value: count,
      color: TIER_COLORS[tier],
    }));

  const countryData = Object.entries(data.country_distribution)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10)
    .map(([country, count]) => ({
      name: COUNTRY_NAMES[country] || country,
      value: count,
    }));

  // Available countries for filter
  const availableCountries = Object.keys(data.country_distribution)
    .filter((c) => c !== 'Unknown')
    .sort((a, b) => {
      const nameA = COUNTRY_NAMES[a] || a;
      const nameB = COUNTRY_NAMES[b] || b;
      return nameA.localeCompare(nameB);
    });

  return (
    <div className="space-y-6">
      {/* Country Filter */}
      <div className="flex items-center gap-4">
        <Globe size={20} className="text-gray-400" />
        <select
          value={selectedCountry}
          onChange={(e) => setSelectedCountry(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        >
          <option value="">All Countries</option>
          {availableCountries.map((country) => (
            <option key={country} value={country}>
              {COUNTRY_NAMES[country] || country}
            </option>
          ))}
        </select>
        {selectedCountry && (
          <button
            onClick={() => setSelectedCountry('')}
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Clear filter
          </button>
        )}
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Users"
          value={data.total_users}
          icon={<Users size={24} />}
          color="blue"
        />
        <MetricCard
          title="New This Week"
          value={data.new_signups.week}
          subtitle={`${data.new_signups.month} this month`}
          icon={<UserPlus size={24} />}
          color="green"
        />
        <MetricCard
          title="Daily Active"
          value={data.active_users.dau}
          subtitle={`${data.active_users.wau} weekly / ${data.active_users.mau} monthly`}
          icon={<Activity size={24} />}
          color="purple"
        />
        <MetricCard
          title="7-Day Retention"
          value={`${data.retention.day7}%`}
          subtitle={`30-day: ${data.retention.day30}% / 90-day: ${data.retention.day90}%`}
          color="yellow"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <LineChart
          data={growthChartData}
          xKey="date"
          yKey="signups"
          title="User Signups (Last 30 Days)"
          color="#0077B6"
        />
        <PieChart
          data={authProviderData}
          title="Signups by Auth Provider"
        />
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PieChart
          data={tierData}
          title="Subscription Tier Distribution"
        />
        {countryData.length > 0 && !selectedCountry && (
          <BarChart
            data={countryData}
            xKey="name"
            yKey="value"
            title="Top Countries"
          />
        )}
      </div>

      {/* Retention Details */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Retention Metrics</h3>
        <div className="grid grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900">{data.retention.day7}%</div>
            <div className="text-sm text-gray-500 mt-1">7-Day Retention</div>
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full"
                style={{ width: `${data.retention.day7}%` }}
              />
            </div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900">{data.retention.day30}%</div>
            <div className="text-sm text-gray-500 mt-1">30-Day Retention</div>
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-yellow-500 rounded-full"
                style={{ width: `${data.retention.day30}%` }}
              />
            </div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-gray-900">{data.retention.day90}%</div>
            <div className="text-sm text-gray-500 mt-1">90-Day Retention</div>
            <div className="mt-2 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-orange-500 rounded-full"
                style={{ width: `${data.retention.day90}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
